<template>
    <ion-page>
        <ion-header>
            <ion-toolbar color="primary">
                <ion-buttons slot="start">
                    <ion-back-button :text="$t('report_sheets.steps_page.back_button_label')" default-href="/report-sheets"></ion-back-button>
                </ion-buttons>
                <ion-title>{{ $t('report_sheets.top_bar.title') }} : {{ sheet?.title ?? $t('report_sheets.top_bar.loading') }}</ion-title>
            </ion-toolbar>
        </ion-header>

        <ion-content>
            <ContentSidebarWrapper side="left">
                <template v-slot:content>
                    <div v-if="loading">
                        <EquipmentSkeleton/>
                    </div>
                    <div v-else>
                        <ion-item :button="true" @click="$router.push(`/report-sheets/${sheet?.uuid}/annexes/${sheet?.annexes[0]?.uuid}`)">
                            <div :class="$style['inner']">
                                <div>
                                    <h4>{{ $t('report_sheets.steps_page.steps.annexes.title') }}</h4>
                                </div>

                                <div :class="$style['right']">
                                    <p v-if="!sheet?.annexes_step_validated">{{ $t('report_sheets.steps_page.steps.pending') }}</p>
                                    <p v-if="sheet?.annexes_step_validated" :class="$style['step-validated']">{{ $t('report_sheets.steps_page.steps.validated') }}</p>
                                </div>
                            </div>
                        </ion-item>

                        <ion-item :button="true" @click="$router.push(`/report-sheets/${sheet?.uuid}/equipments`)">
                            <div :class="$style['inner']">
                                <div>
                                    <h4>{{ $t('report_sheets.steps_page.steps.materials.title') }} ({{ sheet?.equipments.length }})</h4>
                                    <p v-if="sheet?.total_equipments_cost">Coût total : {{ sheet.total_equipments_cost }}€</p>
                                </div>

                                <div :class="$style['right']">
                                    <p v-if="sheet?.equipments_step_validated" :class="$style['step-validated']">{{ $t('report_sheets.steps_page.steps.validated') }}</p>
                                </div>
                            </div>
                        </ion-item>

                        <ion-item :button="true" @click="$router.push(`/report-sheets/${sheet?.uuid}/locations`)">
                            <div :class="$style['inner']">
                                <div>
                                    <h4>{{ $t('report_sheets.steps_page.steps.locations.title') }} ({{ sheet?.locations.length }})</h4>
                                    <p v-if="sheet?.total_locations_cost">Coût total : {{ sheet.total_locations_cost }}€</p>
                                </div>

                                <div :class="$style['right']">
                                    <p v-if="sheet?.locations_step_validated" :class="$style['step-validated']">{{ $t('report_sheets.steps_page.steps.validated') }}</p>
                                </div>
                            </div>
                        </ion-item>

                        <ion-item :button="true" @click="$router.push(`/report-sheets/${sheet?.uuid}/outsourced-works`)">
                            <div :class="$style['inner']">
                                <div>
                                    <h4>{{ $t('report_sheets.steps_page.steps.outsourced_works.title') }} ({{ sheet?.outsourced_works.length }})</h4>
                                    <p v-if="sheet?.total_outsourced_work_cost">Coût total : {{ sheet.total_outsourced_work_cost }}€</p>
                                </div>

                                <div :class="$style['right']">
                                    <p v-if="sheet?.outsourced_work_step_validated" :class="$style['step-validated']">{{ $t('report_sheets.steps_page.steps.validated') }}</p>
                                </div>
                            </div>
                        </ion-item>

                        <ion-item :button="true" @click="$router.push(`/report-sheets/${sheet?.uuid}/employees`)">
                            <div :class="$style['inner']">
                                <div>
                                    <h4>{{ $t('report_sheets.steps_page.steps.employees.title') }} ({{ sheet?.employees.length }})</h4>
                                    <p v-if="sheet?.total_employees_cost">Coût total : {{ sheet.total_employees_cost }}€</p>
                                </div>

                                <div :class="$style['right']">
                                    <p v-if="sheet?.employees_step_validated" :class="$style['step-validated']">{{ $t('report_sheets.steps_page.steps.validated') }}</p>
                                </div>
                            </div>
                        </ion-item>

                        <ion-item :button="true" @click="$router.push(`/report-sheets/${sheet?.uuid}/summary`)">
                            <div :class="$style['inner']">
                                <div>
                                    <h4>{{ $t('report_sheets.steps_page.steps.resume.title') }}</h4>
                                </div>
                            </div>
                        </ion-item>

                        <span v-if="canBeValidatedBySiteSupervisor || canBeValidatedBySiteManager || canBeValidatedByManager" :class="$style['container-button-remove-employee']">
                            <ion-button @click="presentActionSheet" slot="end">
                                Valider ce rapport
                                <ion-icon :class="$style['icon-remove-employee']" :icon="checkmarkOutline"></ion-icon>
                            </ion-button>
                        </span>
                    </div>
                </template>

                <template v-slot:sidebar v-if="!loading && sheet?.total_cost ">
                    <p :class="$style['total_day']">Coût total : {{ sheet?.total_cost ?? 0.00 }}€</p>
                </template>
            </ContentSidebarWrapper>
        </ion-content>
    </ion-page>
</template>

<script lang="ts">
    import {
        IonBackButton,
        IonButtons,
        IonContent,
        IonHeader,
        IonPage,
        IonToolbar,
        toastController,
        alertController,
        IonIcon
    } from "@ionic/vue"

    import {
        caretUp, checkmarkOutline
    } from 'ionicons/icons'

    import { defineComponent } from "vue"
    import ContentSidebarWrapper from "@/components/wrappers/ContentSidebarWrapper.vue"
    import { ReportSheet } from "@/report-sheets"
    import EquipmentSkeleton from "@/components/skeletons/EquipmentSkeleton.vue"
    import {useUserStore} from "@/stores/user";

    export default defineComponent({
        components: {
            ContentSidebarWrapper,
            IonBackButton,
            IonButtons,
            IonContent,
            IonHeader,
            IonPage,
            EquipmentSkeleton,
            IonToolbar,
            IonIcon
        },
        setup() {
          const user = useUserStore();

          return { user, caretUp, checkmarkOutline }
        },
        data() {
            return {
                sheet: null as ReportSheet | null,
                loading: false as boolean | false,
                canBeValidatedBySiteSupervisor: false as boolean | false,
                canBeValidatedBySiteManager: false as boolean | false,
                canBeValidatedByManager: false as boolean | false
            };
        },
        mounted() {
            if (this.user.is_logged_in)
                this.load()

            this.$watch("user.is_logged_in", (is_logged_in: boolean) => {
                if (is_logged_in) this.load();
            });
        },
        ionViewWillEnter() {
            if (this.user.is_logged_in)
                this.load()

            console.log('ionViewWillEnter')
        },
        methods: {
            load() {
                if (this.loading)
                    return

                this.loading = true

                this.axios.get("/report-sheets/" + this.$route.params.uuidReport)
                .then((response) => {
                    this.sheet = response.data

                    this.checkIfcanBeValidated()
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.loading = false
                })
            },
            checkIfcanBeValidated () {
                this.canBeValidatedBySiteSupervisor = false
                this.canBeValidatedBySiteManager = true
                this.canBeValidatedByManager = false

                if (
                    this.sheet?.roles?.find(role => role.user?.uuid === this.user.uuid)?.role == 'site-supervisor' &&
                    this.sheet.status == 'pending'
                )
                    this.canBeValidatedBySiteSupervisor = true

                if (
                    this.sheet?.validated_by_site_supervisor && 
                    !this.sheet?.validated_by_site_manager && 
                    this.sheet?.roles?.find(role => role.user?.uuid === this.user.uuid)?.role == 'site-manager' &&
                    this.sheet?.annexes_step_validated && 
                    this.sheet?.employees_step_validated && 
                    this.sheet?.equipments_step_validated && 
                    this.sheet?.locations_step_validated && 
                    this.sheet?.outsourced_work_step_validated
                )
                    this.canBeValidatedBySiteManager = true
                    
                if (this.sheet?.validated_by_site_manager && !this.sheet?.validated_by_manager && this.sheet?.roles?.find(role => role.user?.uuid === this.user.uuid)?.role == 'manager')
                    this.canBeValidatedByManager = true
            },
            async presentActionSheet() {
                const alert = await alertController.create({
                    message: this.$t('report_sheets.steps_page.alert.message'),
                    htmlAttributes: {
                        'aria-label': 'alert dialog'
                    },
                    buttons: [
                        {
                            text: this.$t('report_sheets.steps_page.alert.buttons.cancel'),
                            role: "cancel"
                        },
                        {
                            text: this.$t('report_sheets.steps_page.alert.buttons.confirm'),
                            role: "confirm",
                            handler: () => {
                                this.reportValidation()
                            }
                        }
                    ]
                })

                await alert.present()
            },
            reportValidation() {
                this.loading = true

                this.axios.patch(`/report-sheets/${this.$route.params.uuidReport}/validation`)
                .then(async (response) => {
                    this.sheet = response.data
                    this.checkIfcanBeValidated()

                    const toast = await toastController.create({
                        header: this.$t('report_sheets.steps_page.validation.title'),
                        message: this.$t('report_sheets.steps_page.validation.message'),
                        duration: 5000,
                        color: "success"
                    })
                    toast.present()
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.loading = false
                })
            }
        }
    })
</script>

<style lang="scss" module>
    .inner {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
        padding: 1em;
        font-size: .9em;

        @media(max-width: 990px) {
            font-size: .8em;
        }
        @media(max-width: 700px) {
            flex-direction: column
        }
        h4, p {
            padding: 0;
            margin: 0;
        }
        p {
            margin-top: .25em;
            color: var(--ion-color-medium);
        }
        .right {
            display: flex;
            align-items: center;

            @media(min-width: 701px) {
                flex-direction: column;
                align-items: flex-end;
            }

            @media(max-width: 700px) {
                .date {
                    &:before {
                        content: '—';
                        margin: 0 .5em;
                        margin-top: 0;
                    }
                }
            }
        }
    }
    .step-validated {
        color: var(--ion-color-success) !important;
        font-weight: bold;
    }
    .container-button-remove-employee {
        margin: 14px 14px 0 0;
        display: flex;
        justify-content: end;
    }
    .icon-remove-employee {
        margin: 0 0 0 14px;
    }
    .total_day {
        color: #fff;
        background: #9e9e9e;
        margin: 0;
        padding: 10px;
        text-align: center;
    }
</style>